import {
  Alert,
  DateRangePickerProps,
  SpaceBetween,
  TextContent,
} from "@amzn/awsui-components-console";
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import ClaimsSearchBar from "src/components/claimsSearchBar/ClaimsSearchBar";
import LoadingIndicator from "src/components/loadingIndicator/LoadingIndicator";
import SearchResults from "src/components/searchResults/SearchResults";
import SimLinkGenerator from "src/components/simLinkGenerator/SimLinkGenerator";
import { SIM_TYPES } from "src/components/simLinkGenerator/types";
import { CountryFilter } from "src/components/workersCompensationClaimsHistory/CountryFilter";
import type { CountryCode } from "src/components/workersCompensationClaimsHistory/constants";
import {
  getDefaultSelectedCountries,
  getUserAccessibleCountries,
  shouldShowCountryFilter,
} from "src/components/workersCompensationClaimsHistory/countryUtils";
import { MetricsPublisher } from "src/metrics";
import { useAppSelector } from "src/redux/hooks";
import type { RootState } from "src/redux/store";
import "src/styles/CountryFilter.scss";

import { executeSearch } from "./claimsSearchDispatcher";
import type { DispatchOutcome } from "./claimsSearchDispatcher";
import { buildColumnDefinitions } from "./columnBuilder";
import ConfigDrivenExportButton from "./ConfigDrivenExportButton";
import ConfigErrorPanel from "./ConfigErrorPanel";
import { toPickerValue, toSearchDateRange } from "./dateRangeConversion";
import { initialSearchState, searchStateReducer } from "./searchStateReducer";
import type {
  ClaimRecord,
  ExportConfig,
  MetricCounterName,
  MetricDeclaration,
  SearchConfig,
  SearchState,
} from "./types";
import { useSearchConfig } from "./useSearchConfig";

export interface ConfigDrivenClaimsHistoryProps {
  /**
   * Renders My Submitted Claims mode: no search bar at all, and one request for
   * the claims the signed-in user reported.
   */
  readonly mySubmittedClaims?: boolean;
}

/**
 * The outcomes that answer a submission without rows and that the user reads as
 * an alert above the table. `validationError` is absent because it travels
 * through the reducer instead, so the entered term keeps its message beside it.
 */
type SearchFailureAlert = Extract<
  DispatchOutcome,
  { kind: "fieldErrors" | "retrievalError" | "configError" | "unsupportedQuery" }
>;

interface MetricContext {
  readonly searchTerm: string;
  readonly siteCode: string;
}

/** Stands in for an absent site code, matching `publishSiteOfSubmittedClaim`. */
const UNKNOWN_SITE_CODE = "Unknown";

const FIELD_ERRORS_INTRO =
  "There was a problem retrieving your search results. Please verify the following and try again:";

const RETRIEVAL_ERROR_INTRO =
  "There was an error loading your search results. Please verify and try again. If the error " +
  "persists please open a ";

const RETRIEVAL_ERROR_OUTRO = " to get help with this issue.";

function counterNameFor(counterName: MetricCounterName, context: MetricContext): string {
  switch (counterName.kind) {
    case "literal":
      return counterName.value;
    case "searchTerm":
      return context.searchTerm;
    case "userSiteCode":
      return context.siteCode === "" ? UNKNOWN_SITE_CODE : context.siteCode;
  }
}

/**
 * Publishes one counter per declaration. Wrapped per declaration rather than
 * around the loop, so one unavailable publisher does not cost the others their
 * metric, and so a metrics outage never presents as a claims outage.
 */
function publishDeclaredMetrics(
  declarations: readonly MetricDeclaration[],
  context: MetricContext
): void {
  declarations.forEach((declaration) => {
    try {
      new MetricsPublisher(declaration.publisherName).publishNamedCounterMetric(
        counterNameFor(declaration.counterName, context),
        1
      );
    } catch (error) {
      console.error(error);
    }
  });
}

function SearchFailureAlertContent({
  alert,
}: {
  readonly alert: SearchFailureAlert;
}): React.ReactElement {
  switch (alert.kind) {
    case "fieldErrors":
      return (
        <Alert type="error" statusIconAriaLabel="Error" data-testid="search-field-errors-alert">
          <TextContent>
            <div>{FIELD_ERRORS_INTRO}</div>
            <ul>
              {alert.errors.map((error, index) => (
                <li key={`${error.fieldName ?? "field"}-${index}`}>
                  {error.fieldName ? <b>{`${error.fieldName}: `}</b> : null}
                  {error.errorMessage}
                </li>
              ))}
            </ul>
          </TextContent>
        </Alert>
      );

    case "retrievalError":
      return (
        <Alert type="error" statusIconAriaLabel="Error" data-testid="search-retrieval-error-alert">
          <span>
            {RETRIEVAL_ERROR_INTRO}
            <SimLinkGenerator
              simType={SIM_TYPES.REPORT_A_BUG}
              target="_blank"
              ariaLabel="Pressing this link will open a new tab where you will be able to create a ticket for the team to investigate this issue."
            >
              support ticket
            </SimLinkGenerator>
            {RETRIEVAL_ERROR_OUTRO}
          </span>
        </Alert>
      );

    case "configError":
      return (
        <Alert type="error" statusIconAriaLabel="Error" data-testid="search-config-error-alert">
          {alert.message}
        </Alert>
      );

    case "unsupportedQuery":
      // Informational rather than an error: the configuration is correct and the
      // user has a way forward, which is to search on a different field.
      return (
        <Alert type="info" statusIconAriaLabel="Information" data-testid="search-unsupported-alert">
          {alert.message}
        </Alert>
      );
  }
}

interface SearchControlsProps {
  readonly config: SearchConfig;
  readonly mySubmittedClaims: boolean;
  readonly searchState: SearchState;
  readonly showCountryFilter: boolean;
  readonly selectedCountries: readonly CountryCode[];
  readonly accessibleCountries: readonly CountryCode[];
  readonly searchAlert: SearchFailureAlert | null;
  readonly onFieldChange: (fieldId: string) => void;
  readonly onTermChange: (term: string) => void;
  readonly onDateRangeChange: (value: DateRangePickerProps.Value | null) => void;
  readonly onSubmit: () => void;
  readonly onClearError: () => void;
  readonly onCountryChange: (countries: CountryCode[]) => void;
}

/**
 * Everything above the table: the search bar, the country filter beneath it,
 * and the search failure surfaces beneath that.
 */
function SearchControls({
  config,
  mySubmittedClaims,
  searchState,
  showCountryFilter,
  selectedCountries,
  accessibleCountries,
  searchAlert,
  onFieldChange,
  onTermChange,
  onDateRangeChange,
  onSubmit,
  onClearError,
  onCountryChange,
}: SearchControlsProps): React.ReactElement {
  // With no search bar there is nothing to carry a validation message, so the
  // mode renders its own surface for one.
  const showStandaloneValidation = mySubmittedClaims && searchState.validationMessage !== null;

  return (
    <SpaceBetween direction="vertical" size="s">
      {mySubmittedClaims ? null : (
        <ClaimsSearchBar
          fields={config.searchableFields}
          selectedFieldId={searchState.selectedFieldId}
          term={searchState.term}
          dateRange={toPickerValue(searchState.dateRange)}
          errorMessage={searchState.validationMessage ?? undefined}
          onFieldChange={onFieldChange}
          onTermChange={onTermChange}
          onDateRangeChange={onDateRangeChange}
          onSubmit={onSubmit}
          onClearError={onClearError}
        />
      )}
      {showCountryFilter ? (
        <div className="country-filter-container">
          <CountryFilter
            selectedCountries={[...selectedCountries]}
            accessibleCountries={[...accessibleCountries]}
            onChange={onCountryChange}
            testId="claims-history-country-filter"
          />
        </div>
      ) : null}
      {showStandaloneValidation ? (
        <Alert type="error" statusIconAriaLabel="Error" data-testid="search-validation-alert">
          {searchState.validationMessage}
        </Alert>
      ) : null}
      {searchAlert === null ? null : <SearchFailureAlertContent alert={searchAlert} />}
    </SpaceBetween>
  );
}

interface ClaimsHistoryContentProps {
  readonly config: SearchConfig;
  readonly mySubmittedClaims: boolean;
}

/**
 * The page over a validated Search_Config.
 *
 * Search state lives in this component's reducer, not in Redux and not in the
 * URL, and retrieval results are held here under the submission that asked for
 * them rather than selected out of a slice. That is what makes a late outcome
 * discardable: the submission it belongs to is known at the point it arrives.
 */
function ClaimsHistoryContent({
  config,
  mySubmittedClaims,
}: ClaimsHistoryContentProps): React.ReactElement {
  const { user } = useAppSelector((state: RootState) => state.auth);
  const [searchState, dispatch] = useReducer(searchStateReducer, config, initialSearchState);

  /**
   * The state as of the last submission, which is what a request is built from.
   * Held separately because the live state changes on every keystroke: keying
   * retrieval off it directly would search each character, and a country change
   * after a submit would search a term the user had typed but not submitted.
   */
  const [submitted, setSubmitted] = useState<SearchState | null>(null);
  if (searchState.submissionId > 0 && searchState.submissionId !== submitted?.submissionId) {
    setSubmitted(searchState);
  }

  const [rows, setRows] = useState<ClaimRecord[]>([]);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchAlert, setSearchAlert] = useState<SearchFailureAlert | null>(null);
  const [selectedCountries, setSelectedCountries] = useState<CountryCode[] | null>(null);

  const accessibleCountries = useMemo(
    () => getUserAccessibleCountries(user.roles ?? []),
    [user.roles]
  );

  // Null means the user has not touched the control, which is every option
  // selected. An empty array is a deliberate clearing, which is distinct.
  const displayedCountries = selectedCountries ?? getDefaultSelectedCountries(accessibleCountries);

  /**
   * A cleared selection is widened to every country the roles grant. The
   * dispatcher omits the country key for an empty array, and a set matching no
   * claim would render an empty table for a user who simply cleared a filter.
   * The mapping stays in `countryUtils`: which countries a user may see is an
   * access decision, so it does not belong in a document a backend may serve.
   */
  const requestCountries = displayedCountries.length > 0 ? displayedCountries : accessibleCountries;
  const countriesKey = requestCountries.join(",");

  const showCountryFilter =
    config.countryFilter !== undefined && shouldShowCountryFilter(accessibleCountries);

  const applyOutcome = useCallback((outcome: DispatchOutcome) => {
    if (outcome.kind === "superseded") {
      // A newer submission owns the table and the loading state.
      return;
    }

    setIsSearching(false);

    if (outcome.kind === "results") {
      setRows([...outcome.rows]);
      setHasMoreResults(outcome.hasMoreResults);
      setSearchAlert(null);
      return;
    }

    // The submission was answered without rows, so the table empties rather
    // than showing an earlier search's claims beside a failure about this one.
    setRows([]);
    setHasMoreResults(false);

    if (outcome.kind === "validationError") {
      dispatch({ type: "reject", message: outcome.message });
      setSearchAlert(null);
      return;
    }

    setSearchAlert(outcome);
  }, []);

  useEffect(() => {
    const shouldSearch = mySubmittedClaims ? user.id !== "" : submitted !== null;
    if (!shouldSearch) {
      return;
    }

    let cancelled = false;
    setIsSearching(true);

    void executeSearch({
      state: submitted ?? initialSearchState(config),
      config,
      countries: requestCountries,
      ...(mySubmittedClaims ? { mySubmittedClaimsAlias: user.id } : {}),
    }).then((outcome) => {
      // Discards an outcome belonging to a submission this component has moved
      // on from, which is what makes two responses resolving in reverse order
      // leave the later submission's rows on screen.
      if (!cancelled) {
        applyOutcome(outcome);
      }
    });

    return () => {
      cancelled = true;
    };
    // `requestCountries` is read through the closure and tracked by
    // `countriesKey`, so a re-selection of the same countries does not spend a
    // request. `submitted` changes identity once per submission, and never on a
    // keystroke.
  }, [config, submitted, countriesKey, mySubmittedClaims, user.id, applyOutcome]);

  const pageVisitedMetrics =
    mySubmittedClaims && config.mySubmittedClaims
      ? config.mySubmittedClaims.metrics
      : config.metrics.pageVisited;

  const pageVisitedPublished = useRef(false);
  useEffect(() => {
    // Waits for the identity the site-code counter reads, then publishes once.
    if (pageVisitedPublished.current || user.id === "") {
      return;
    }
    pageVisitedPublished.current = true;
    publishDeclaredMetrics(pageVisitedMetrics, { searchTerm: "", siteCode: user.siteCode });
  }, [pageVisitedMetrics, user.id, user.siteCode]);

  const handleSubmit = useCallback(() => {
    // Reached only after the search bar has accepted the term, so a rejected
    // term publishes nothing and issues nothing.
    const context: MetricContext = { searchTerm: searchState.term, siteCode: user.siteCode };
    const field = config.searchableFields.find(
      (candidate) => candidate.fieldId === searchState.selectedFieldId
    );
    publishDeclaredMetrics(field?.metrics ?? [], context);
    publishDeclaredMetrics(config.metrics.searchSubmitted, context);
    dispatch({ type: "submit" });
  }, [config, searchState.selectedFieldId, searchState.term, user.siteCode]);

  const handleFieldChange = useCallback(
    (fieldId: string) => dispatch({ type: "selectField", fieldId }),
    []
  );
  const handleTermChange = useCallback((term: string) => dispatch({ type: "setTerm", term }), []);
  const handleDateRangeChange = useCallback(
    (value: DateRangePickerProps.Value | null) =>
      dispatch({ type: "setDateRange", dateRange: toSearchDateRange(value) }),
    []
  );
  const handleClearError = useCallback(() => setSearchAlert(null), []);
  const handleCountryChange = useCallback(
    (countries: CountryCode[]) => setSelectedCountries(countries),
    []
  );

  const columnDefinitions = useMemo(() => buildColumnDefinitions(config), [config]);

  /**
   * `SearchResults` takes a field name, so a column sorted by a comparator over
   * derived values falls back to the property that comparator reads. Both order
   * by the same underlying value, so the first rendered order is unaffected.
   */
  const defaultSorting = useMemo(() => {
    const { columns, defaultSorting: declared } = config.table;
    const column = columns.find((candidate) => candidate.columnId === declared.columnId);
    const isDescending = declared.direction === "descending";
    if (column === undefined) {
      return { sortingField: declared.columnId, isDescending };
    }
    const sortingField =
      column.sorting.kind === "field" ? column.sorting.sortingField : column.source;
    return { sortingField, isDescending };
  }, [config]);

  const exportConfig = useMemo<ExportConfig>(() => {
    const modePrefix = mySubmittedClaims
      ? config.mySubmittedClaims?.exportFileNamePrefix
      : undefined;
    return modePrefix === undefined
      ? config.exportConfig
      : { ...config.exportConfig, fileNamePrefix: modePrefix };
  }, [config, mySubmittedClaims]);

  const headerContent = useMemo(() => {
    const mode = config.mySubmittedClaims;
    if (mySubmittedClaims && mode) {
      const atLimit = rows.length === mode.pageSize;
      const limitSuffix = atLimit ? mode.limitReachedSuffix : "";
      return `${mode.resultsHeaderText} (${rows.length})${limitSuffix}`;
    }
    if (!hasMoreResults) {
      return config.table.resultsHeaderText;
    }
    // More results applies to every line of business and every searchable
    // field; the date-range advice is only actionable where a range was
    // actually searched.
    const searchedADateRange = submitted !== null && submitted.dateRange !== null;
    const moreResults = config.table.moreResultsSuffix ?? "";
    const narrowAdvice = searchedADateRange ? (config.table.narrowDateRangeSuffix ?? "") : "";
    return `${config.table.resultsHeaderText}${moreResults}${narrowAdvice}`;
  }, [config, mySubmittedClaims, rows.length, hasMoreResults, submitted]);

  return (
    <SearchResults<ClaimRecord>
      items={rows}
      columnDefinitions={columnDefinitions}
      loading={isSearching}
      loadingText={config.table.loadingText}
      resourceName={config.table.resourceName}
      headerContent={headerContent}
      headerActions={<ConfigDrivenExportButton exportConfig={exportConfig} claims={rows} />}
      searchBarComponent={
        <SearchControls
          config={config}
          mySubmittedClaims={mySubmittedClaims}
          searchState={searchState}
          showCountryFilter={showCountryFilter}
          selectedCountries={displayedCountries}
          accessibleCountries={accessibleCountries}
          searchAlert={searchAlert}
          onFieldChange={handleFieldChange}
          onTermChange={handleTermChange}
          onDateRangeChange={handleDateRangeChange}
          onSubmit={handleSubmit}
          onClearError={handleClearError}
          onCountryChange={handleCountryChange}
        />
      }
      filterPlaceholder={config.table.filterPlaceholder}
      filterAriaLabel={config.table.filterAriaLabel}
      defaultSorting={defaultSorting}
      pageSize={config.table.pageSize}
      minItemsForControls={config.table.minItemsForControls}
      testId={config.testId}
    />
  );
}

/**
 * Resolves the Search_Config for the route and renders the page from it.
 *
 * The configuration outcome is handled here and the page body lives in a child,
 * so a search cannot be dispatched without a validated config: on any non-ready
 * outcome the component holding the reducer and the retrieval effect is never
 * mounted, rather than mounted and guarded.
 *
 * The browser location is only read. Nothing here writes to it and no history
 * entry is added, so a submitted search leaves the address bar untouched and a
 * reload lands on the default unsearched state.
 */
function ConfigDrivenClaimsHistory({
  mySubmittedClaims = false,
}: ConfigDrivenClaimsHistoryProps): React.ReactElement | null {
  const { pathname } = useLocation();
  const state = useSearchConfig(pathname);

  if (state.status === "loading") {
    return (
      <LoadingIndicator label="Loading claims history" testId="claims-history-config-loading" />
    );
  }

  if (state.status === "failed") {
    return <ConfigErrorPanel failure={state.failure} />;
  }

  return (
    <ClaimsHistoryContent
      // A line-of-business change starts a new page rather than carrying the
      // previous line of business's term and results into it.
      key={state.lob}
      config={state.config}
      mySubmittedClaims={mySubmittedClaims}
    />
  );
}

export default ConfigDrivenClaimsHistory;
